import { useMemo, useRef, useState } from 'react';
import { supabase } from '../supabase';
import { useTheme } from '../ThemeContext';
import {
  autoMapHeaders,
  buildImportReview,
  importPayload,
  JOB_IMPORT_FIELDS,
  MAX_IMPORT_FILE_BYTES,
  parseCSV,
} from '../lib/jobCsvImport';

const AMBER = '#D97706';
const AMBER_DARK = '#B45309';
const STATUS_COLORS = { ready: '#047857', duplicate: '#A16207', error: '#B91C1C' };

export default function CSVImport({ companyId, stages, onClose, onImported, inline = false }) {
  const { t, darkMode } = useTheme();
  const s = useMemo(() => styles(t, darkMode), [t, darkMode]);
  const [step, setStep] = useState('upload');
  const [fileName, setFileName] = useState('');
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRows, setCsvRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [review, setReview] = useState(null);
  const [filter, setFilter] = useState('all');
  const [confirmed, setConfirmed] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [result, setResult] = useState(null);
  const [undone, setUndone] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const idempotencyKeyRef = useRef(null);

  const resetFile = () => {
    setCsvHeaders([]);
    setCsvRows([]);
    setReview(null);
    setConfirmed(false);
    setError('');
    setStep('upload');
    idempotencyKeyRef.current = null;
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFile = (file) => {
    if (!file || !file.name.toLowerCase().endsWith('.csv')) {
      setError('Choose a CSV file.');
      return;
    }
    if (file.size > MAX_IMPORT_FILE_BYTES) {
      setError('The CSV must be 5 MB or smaller.');
      return;
    }
    setError('');
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const parsed = parseCSV(event.target.result);
        setFileName(file.name);
        setCsvHeaders(parsed.headers);
        setCsvRows(parsed.rows);
        setMapping(autoMapHeaders(parsed.headers));
        setReview(null);
        setConfirmed(false);
        idempotencyKeyRef.current = crypto.randomUUID();
        setStep('map');
      } catch (parseError) {
        setError(parseError.message);
      }
    };
    reader.onerror = () => setError('TrackShop could not read this file.');
    reader.readAsText(file);
  };

  const prepareReview = async () => {
    if (!mapping.jobName || !mapping.customerName) {
      setError('Map both Job Name and Customer Name before continuing.');
      return;
    }
    setStep('checking');
    setError('');
    const { data: existingJobs, error: lookupError } = await supabase
      .from('jobs')
      .select('job_number, name, po_number, customers(name)')
      .eq('company_id', companyId)
      .limit(10000);
    if (lookupError) {
      setError(`Could not check existing jobs: ${lookupError.message}`);
      setStep('map');
      return;
    }
    setReview(buildImportReview({
      headers: csvHeaders,
      rows: csvRows,
      mapping,
      stages,
      existingJobs: existingJobs ?? [],
    }));
    setFilter('all');
    setConfirmed(false);
    setStep('review');
  };

  const handleImport = async () => {
    if (!review?.readyRows.length || review.errorRows.length || !confirmed) return;
    setStep('importing');
    setError('');
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) {
      setError('Your session expired. Sign in again before importing.');
      setStep('review');
      return;
    }
    const { data, error: importError } = await supabase.rpc('import_jobs_from_csv', {
      p_company_id: companyId,
      p_user_id: session.user.id,
      p_idempotency_key: idempotencyKeyRef.current,
      p_file_name: fileName,
      p_rows: importPayload(review),
    });
    if (importError) {
      setError(`Nothing was imported. ${importError.message}`);
      setStep('review');
      return;
    }
    setResult(data);
    setStep('done');
  };

  const undoImport = async () => {
    if (!result?.batch_id || undone) return;
    setError('');
    const { error: undoError } = await supabase.rpc('rollback_job_import', { p_batch_id: result.batch_id });
    if (undoError) {
      setError(`Could not undo the import: ${undoError.message}`);
      return;
    }
    setUndone(true);
  };

  const finish = () => {
    onImported?.(result, { undone });
    onClose?.();
  };

  const visibleRows = review?.rows.filter((row) => filter === 'all' || row.status === filter) ?? [];
  const canImport = review?.readyRows.length > 0 && review.errorRows.length === 0 && confirmed;
  const stepNumber = { upload: 1, map: 2, checking: 2, review: 3, importing: 4, done: 4 }[step];

  const inner = (
    <div className={`csv-import-modal${inline ? ' csv-import-inline' : ''}`} style={inline ? s.inline : s.modal} role={inline ? undefined : 'dialog'} aria-modal={inline ? undefined : 'true'} aria-labelledby="csv-import-title">
      <style>{`
        @media (max-width: 700px) {
          .csv-import-modal:not(.csv-import-inline) { width: 100% !important; max-height: calc(100vh - 16px) !important; }
          .csv-import-modal .csv-map-grid { grid-template-columns: 1fr !important; }
          .csv-import-modal .csv-summary-grid { grid-template-columns: 1fr !important; }
          .csv-import-modal .csv-import-footer { flex-direction: column-reverse !important; }
          .csv-import-modal .csv-import-footer button { width: 100% !important; }
        }
      `}</style>
      <div style={s.header}>
        <div style={{ minWidth: 0 }}>
          <div style={s.eyebrow}>DATA IMPORT · STEP {stepNumber} OF 4</div>
          <h2 id="csv-import-title" style={s.title}>Import jobs</h2>
          <p style={s.subtitle}>
            {step === 'upload' && 'Select the spreadsheet export you want to review.'}
            {(step === 'map' || step === 'checking') && `${csvRows.length.toLocaleString()} rows found in ${fileName}.`}
            {step === 'review' && 'Review every issue before anything is added to your shop.'}
            {step === 'importing' && 'Committing the validated jobs as one transaction.'}
            {step === 'done' && (undone ? 'This import has been moved to the archive.' : 'The validated jobs are now in TrackShop.')}
          </p>
        </div>
        {step !== 'importing' && !inline && <button onClick={onClose} style={s.iconBtn} aria-label="Close import">×</button>}
      </div>

      <div style={s.stepTrack} aria-hidden="true">
        {[1, 2, 3, 4].map((number) => <span key={number} style={{ ...s.stepSegment, ...(number <= stepNumber ? s.stepSegmentActive : {}) }} />)}
      </div>

      <div style={s.body}>
        {step === 'upload' && (
          <div>
            <button
              type="button"
              style={{ ...s.dropZone, ...(dragOver ? s.dropZoneActive : {}) }}
              onDragOver={(event) => { event.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(event) => { event.preventDefault(); setDragOver(false); handleFile(event.dataTransfer.files[0]); }}
              onClick={() => fileInputRef.current?.click()}
            >
              <span style={s.uploadMark}>↑</span>
              <span style={s.dropText}>Choose a CSV file</span>
              <span style={s.dropSub}>or drop it here · up to 1,000 jobs</span>
            </button>
            <input ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={(event) => handleFile(event.target.files[0])} style={{ display: 'none' }} />
            <div style={s.protectionRow}>
              <span style={s.protectionIcon}>✓</span>
              <div><strong style={{ color: t.text2 }}>Nothing is imported on upload.</strong><span style={{ color: t.text3 }}> You will map columns, resolve errors, and confirm the final count first.</span></div>
            </div>
          </div>
        )}

        {(step === 'map' || step === 'checking') && (
          <div>
            <div style={s.fileBar}>
              <div><strong style={{ color: t.text1 }}>{fileName}</strong><span style={s.fileMeta}>{csvRows.length.toLocaleString()} data rows · {csvHeaders.length} columns</span></div>
              <button type="button" onClick={resetFile} style={s.linkBtn} disabled={step === 'checking'}>Replace file</button>
            </div>
            <div className="csv-map-grid" style={s.mapGrid}>
              {JOB_IMPORT_FIELDS.map((field) => (
                <label key={field.key} style={s.mapField}>
                  <span style={s.mapLabel}>{field.label}{field.required && <span style={s.required}> Required</span>}</span>
                  <select value={mapping[field.key] ?? ''} onChange={(event) => setMapping((current) => ({ ...current, [field.key]: event.target.value }))} style={s.select} disabled={step === 'checking'}>
                    <option value="">Do not import</option>
                    {csvHeaders.map((header) => <option key={header} value={header}>{header}</option>)}
                  </select>
                </label>
              ))}
            </div>
            {step === 'checking' && <div style={s.processing}><span style={s.spinner} />Checking rows and existing jobs…</div>}
          </div>
        )}

        {step === 'review' && review && (
          <div>
            <div className="csv-summary-grid" style={s.summaryGrid}>
              <Summary value={review.readyRows.length} label="Ready to import" color="#047857" s={s} />
              <Summary value={review.duplicateRows.length} label="Duplicates skipped" color="#A16207" s={s} />
              <Summary value={review.errorRows.length} label="Rows to fix" color="#B91C1C" s={s} />
            </div>
            <div style={s.reviewToolbar}>
              <div style={s.filters}>
                {[
                  ['all', 'All', review.rows.length],
                  ['ready', 'Ready', review.readyRows.length],
                  ['duplicate', 'Duplicates', review.duplicateRows.length],
                  ['error', 'Needs attention', review.errorRows.length],
                ].map(([value, label, count]) => (
                  <button key={value} type="button" onClick={() => setFilter(value)} style={{ ...s.filterBtn, ...(filter === value ? s.filterBtnActive : {}) }}>{label} <span style={s.filterCount}>{count}</span></button>
                ))}
              </div>
              <button type="button" onClick={resetFile} style={s.linkBtn}>Replace file</button>
            </div>
            <div style={s.tableWrap}>
              <table className="csv-review-table" style={s.table}>
                <thead><tr>{['Row', 'Status', 'Job', 'Customer', 'PO', 'Value', 'Due', 'Stage'].map((heading) => <th key={heading} style={s.th}>{heading}</th>)}</tr></thead>
                <tbody>
                  {visibleRows.slice(0, 250).map((row) => (
                    <tr key={row.rowNumber} style={s.tr}>
                      <td style={s.rowNumber}>{row.rowNumber}</td>
                      <td style={s.td}><span style={{ ...s.status, color: STATUS_COLORS[row.status], borderColor: `${STATUS_COLORS[row.status]}55`, backgroundColor: `${STATUS_COLORS[row.status]}12` }}>{row.status === 'ready' ? 'Ready' : row.status === 'duplicate' ? 'Skip' : 'Fix'}</span></td>
                      <td style={s.td}><strong style={{ color: t.text1 }}>{row.jobNumber || 'Auto number'}</strong><span style={s.cellSub}>{row.jobName || 'Missing job name'}</span>{row.issues.length > 0 && <span style={s.issue}>{row.issues.join(' ')}</span>}{row.duplicateReason && <span style={s.warning}>{row.duplicateReason}</span>}</td>
                      <td style={s.td}>{row.customerName || '—'}</td>
                      <td style={s.td}>{row.poNumber || '—'}</td>
                      <td style={s.td}>{row.estimatedValue == null ? '—' : `$${row.estimatedValue.toLocaleString('en-US')}`}</td>
                      <td style={s.td}>{row.dueDate || '—'}</td>
                      <td style={s.td}>{row.stageName || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {visibleRows.length > 250 && <p style={s.tableNote}>Showing the first 250 matching rows. All {visibleRows.length.toLocaleString()} rows were validated.</p>}
            {review.errorRows.length > 0 ? (
              <div style={s.blocker}><strong>{review.errorRows.length} row{review.errorRows.length === 1 ? '' : 's'} need attention.</strong> Correct the source CSV or column mapping before importing. No jobs have been added.</div>
            ) : (
              <label style={s.confirmRow}>
                <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} style={s.checkbox} />
                <span>I reviewed the file. Import {review.readyRows.length.toLocaleString()} job{review.readyRows.length === 1 ? '' : 's'} and skip {review.duplicateRows.length.toLocaleString()} duplicate{review.duplicateRows.length === 1 ? '' : 's'}.</span>
              </label>
            )}
          </div>
        )}

        {step === 'importing' && <div style={s.importing}><span style={s.spinnerLarge} /><h3 style={s.processingTitle}>Importing {review.readyRows.length.toLocaleString()} jobs</h3><p style={s.processingCopy}>The entire import will succeed together or no jobs will be added.</p></div>}

        {step === 'done' && result && (
          <div style={s.done}>
            <div style={{ ...s.doneMark, ...(undone ? s.doneMarkMuted : {}) }}>{undone ? '↶' : '✓'}</div>
            <h3 style={s.doneTitle}>{undone ? 'Import undone' : `${result.inserted_count.toLocaleString()} jobs imported`}</h3>
            <p style={s.doneCopy}>{undone ? 'The imported jobs are in the archive and can be restored individually.' : `${result.skipped_count.toLocaleString()} server-side duplicate${result.skipped_count === 1 ? ' was' : 's were'} skipped. This batch remains available in the audit trail.`}</p>
            {!undone && <button type="button" onClick={undoImport} style={s.undoBtn}>Undo this import</button>}
          </div>
        )}

        {error && <div role="alert" style={s.error}>{error}</div>}
      </div>

      <div className="csv-import-footer" style={s.footer}>
        {step === 'upload' && <button type="button" onClick={onClose} style={s.secondaryBtn}>Cancel</button>}
        {step === 'map' && <><button type="button" onClick={resetFile} style={s.secondaryBtn}>Back</button><button type="button" onClick={prepareReview} style={s.primaryBtn}>Review rows</button></>}
        {step === 'checking' && <button type="button" style={s.primaryBtnDisabled} disabled>Checking…</button>}
        {step === 'review' && <><button type="button" onClick={() => { setStep('map'); setConfirmed(false); }} style={s.secondaryBtn}>Back to mapping</button><button type="button" onClick={handleImport} style={canImport ? s.primaryBtn : s.primaryBtnDisabled} disabled={!canImport}>Import {review.readyRows.length.toLocaleString()} validated job{review.readyRows.length === 1 ? '' : 's'}</button></>}
        {step === 'done' && <button type="button" onClick={finish} style={s.primaryBtn}>{inline ? 'Continue setup' : 'Return to jobs'}</button>}
      </div>
    </div>
  );

  if (inline) return inner;
  return <div style={s.overlay} onMouseDown={(event) => event.target === event.currentTarget && step !== 'importing' && onClose()}>{inner}</div>;
}

function Summary({ value, label, color, s }) {
  return <div style={s.summary}><span style={{ ...s.summaryValue, color }}>{value.toLocaleString()}</span><span style={s.summaryLabel}>{label}</span></div>;
}

function styles(t, darkMode) {
  const button = { minHeight: 40, padding: '9px 16px', borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' };
  return {
    overlay: { position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, backgroundColor: t.overlay },
    modal: { width: 'min(1040px, 100%)', maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: 8, border: `1px solid ${t.border1}`, backgroundColor: t.card, boxShadow: t.shadowLg },
    inline: { width: '100%', overflow: 'hidden', borderRadius: 8, border: `1px solid ${t.border1}`, backgroundColor: t.card },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24, padding: '22px 24px 18px' },
    eyebrow: { marginBottom: 7, color: AMBER, fontSize: 10, fontWeight: 800, letterSpacing: '0.08em' },
    title: { margin: 0, color: t.text1, fontSize: 21, lineHeight: 1.25, fontWeight: 750, letterSpacing: 0 },
    subtitle: { margin: '6px 0 0', color: t.text3, fontSize: 13, lineHeight: 1.5 },
    iconBtn: { width: 36, height: 36, flexShrink: 0, borderRadius: 6, border: `1px solid ${t.border1}`, color: t.text3, backgroundColor: t.card2, cursor: 'pointer', fontSize: 20, lineHeight: 1 },
    stepTrack: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4, padding: '0 24px 18px' },
    stepSegment: { height: 3, borderRadius: 3, backgroundColor: t.border2 },
    stepSegmentActive: { backgroundColor: AMBER },
    body: { padding: 24, overflow: 'auto', borderTop: `1px solid ${t.border1}`, minHeight: 260 },
    dropZone: { width: '100%', minHeight: 210, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 28, borderRadius: 8, border: `1px dashed ${t.borderInput}`, backgroundColor: t.card2, cursor: 'pointer', fontFamily: 'inherit' },
    dropZoneActive: { borderColor: AMBER, backgroundColor: darkMode ? '#23190E' : '#FFFBEB' },
    uploadMark: { width: 42, height: 42, display: 'grid', placeItems: 'center', marginBottom: 14, borderRadius: 7, color: darkMode ? '#111827' : '#fff', backgroundColor: darkMode ? '#F59E0B' : AMBER, fontSize: 22, fontWeight: 800 },
    dropText: { color: t.text1, fontSize: 15, fontWeight: 750 },
    dropSub: { marginTop: 5, color: t.text3, fontSize: 12 },
    protectionRow: { display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 14, padding: '12px 14px', border: `1px solid ${t.border1}`, borderRadius: 6, backgroundColor: t.card, fontSize: 12, lineHeight: 1.5 },
    protectionIcon: { width: 19, height: 19, display: 'grid', placeItems: 'center', flexShrink: 0, borderRadius: '50%', color: '#fff', backgroundColor: '#047857', fontSize: 11, fontWeight: 800 },
    fileBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '12px 14px', marginBottom: 18, border: `1px solid ${t.border1}`, borderRadius: 6, backgroundColor: t.card2, fontSize: 13 },
    fileMeta: { display: 'block', marginTop: 3, color: t.text3, fontSize: 11 },
    linkBtn: { padding: 0, border: 0, color: AMBER, background: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' },
    mapGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 },
    mapField: { display: 'grid', gap: 6 },
    mapLabel: { color: t.text2, fontSize: 12, fontWeight: 700 },
    required: { marginLeft: 5, color: AMBER, fontSize: 10, textTransform: 'uppercase' },
    select: { width: '100%', minHeight: 42, padding: '8px 10px', borderRadius: 6, border: `1px solid ${t.borderInput}`, color: t.text1, backgroundColor: t.card, fontFamily: 'inherit', fontSize: 13, outline: 'none' },
    processing: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9, marginTop: 20, color: t.text3, fontSize: 13 },
    spinner: { width: 15, height: 15, border: `2px solid ${t.border1}`, borderTopColor: AMBER, borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
    summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10, marginBottom: 16 },
    summary: { padding: '13px 14px', border: `1px solid ${t.border1}`, borderRadius: 6, backgroundColor: t.card2 },
    summaryValue: { display: 'block', fontSize: 22, fontWeight: 800, lineHeight: 1.1 },
    summaryLabel: { display: 'block', marginTop: 5, color: t.text3, fontSize: 11, fontWeight: 600 },
    reviewToolbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 10 },
    filters: { display: 'flex', alignItems: 'center', gap: 4, overflowX: 'auto' },
    filterBtn: { minHeight: 34, padding: '6px 9px', border: '1px solid transparent', borderRadius: 6, color: t.text3, backgroundColor: 'transparent', cursor: 'pointer', fontFamily: 'inherit', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' },
    filterBtnActive: { color: t.text1, borderColor: t.border1, backgroundColor: t.card2 },
    filterCount: { marginLeft: 3, color: t.text4 },
    tableWrap: { maxHeight: 330, overflow: 'auto', border: `1px solid ${t.border1}`, borderRadius: 6 },
    table: { width: '100%', minWidth: 900, borderCollapse: 'separate', borderSpacing: 0, fontSize: 12 },
    th: { position: 'sticky', top: 0, zIndex: 1, padding: '9px 10px', borderBottom: `1px solid ${t.border1}`, color: t.text3, backgroundColor: t.card2, textAlign: 'left', textTransform: 'uppercase', fontSize: 9, fontWeight: 800, letterSpacing: '0.08em' },
    tr: { backgroundColor: t.card },
    td: { padding: '10px', borderBottom: `1px solid ${t.border2}`, color: t.text2, verticalAlign: 'top', whiteSpace: 'nowrap' },
    rowNumber: { padding: '10px', borderBottom: `1px solid ${t.border2}`, color: t.text4, verticalAlign: 'top', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' },
    cellSub: { display: 'block', maxWidth: 220, marginTop: 3, overflow: 'hidden', color: t.text3, textOverflow: 'ellipsis' },
    issue: { display: 'block', maxWidth: 300, marginTop: 5, color: '#B91C1C', whiteSpace: 'normal', lineHeight: 1.35 },
    warning: { display: 'block', marginTop: 5, color: '#A16207' },
    status: { display: 'inline-flex', padding: '3px 6px', border: '1px solid', borderRadius: 4, fontSize: 9, fontWeight: 800, textTransform: 'uppercase' },
    tableNote: { margin: '9px 0 0', color: t.text4, fontSize: 11 },
    blocker: { marginTop: 14, padding: '11px 13px', border: `1px solid ${darkMode ? '#7F1D1D' : '#FECACA'}`, borderRadius: 6, color: darkMode ? '#FCA5A5' : '#991B1B', backgroundColor: darkMode ? '#260E12' : '#FEF2F2', fontSize: 12, lineHeight: 1.5 },
    confirmRow: { display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 14, padding: '12px 13px', border: `1px solid ${t.border1}`, borderRadius: 6, color: t.text2, backgroundColor: t.card2, cursor: 'pointer', fontSize: 12, lineHeight: 1.5 },
    checkbox: { width: 16, height: 16, margin: '1px 0 0', accentColor: AMBER, flexShrink: 0 },
    importing: { minHeight: 260, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' },
    spinnerLarge: { width: 34, height: 34, border: `3px solid ${t.border1}`, borderTopColor: AMBER, borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
    processingTitle: { margin: '18px 0 6px', color: t.text1, fontSize: 17, letterSpacing: 0 },
    processingCopy: { margin: 0, color: t.text3, fontSize: 12 },
    done: { minHeight: 260, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' },
    doneMark: { width: 46, height: 46, display: 'grid', placeItems: 'center', borderRadius: '50%', color: '#fff', backgroundColor: '#047857', fontSize: 22, fontWeight: 800 },
    doneMarkMuted: { backgroundColor: t.text3 },
    doneTitle: { margin: '16px 0 7px', color: t.text1, fontSize: 20, letterSpacing: 0 },
    doneCopy: { maxWidth: 500, margin: 0, color: t.text3, fontSize: 13, lineHeight: 1.55 },
    undoBtn: { marginTop: 16, padding: 0, border: 0, color: '#B91C1C', background: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700 },
    error: { marginTop: 14, padding: '10px 12px', border: `1px solid ${darkMode ? '#7F1D1D' : '#FECACA'}`, borderRadius: 6, color: darkMode ? '#FCA5A5' : '#991B1B', backgroundColor: darkMode ? '#260E12' : '#FEF2F2', fontSize: 12, lineHeight: 1.45 },
    footer: { display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '14px 24px', borderTop: `1px solid ${t.border1}`, backgroundColor: t.card2 },
    secondaryBtn: { ...button, color: t.text2, border: `1px solid ${t.borderInput}`, backgroundColor: t.card },
    primaryBtn: { ...button, color: '#fff', border: `1px solid ${AMBER_DARK}`, backgroundColor: AMBER },
    primaryBtnDisabled: { ...button, color: t.text4, border: `1px solid ${t.border1}`, backgroundColor: t.border2, cursor: 'not-allowed' },
  };
}
